import React, { ReactNode } from "react";
import { NotificationsDataState, NotificationsViewState } from "../redux/state";
import { DefaultNotificationRenderer } from "./NotificationRenderers/DefaultNotificationRenderer";
import { Notification, NotificationProps } from "./Notification";
import { OfqualNotification } from "../services/domain/OfqualNotification";
import { makeStyles } from "@material-ui/core";
import { OrganisationRenderer } from "./NotificationRenderers/OrganisationRenderer";
import { EventNotificationRenderer } from "./NotificationRenderers/EventNotificationRenderer";
import { NotificationType } from "../services/domain/NotificationType";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toggle } from "../redux/actions";
import { InformationRequestRenderer } from "./NotificationRenderers/InformationRequestRenderer";
import { CtrRenderer } from "./NotificationRenderers/CtrRenderer";
import { QualificationRenderer } from "./NotificationRenderers/QualificationRenderer";
import { ComplaintRenderer } from "./NotificationRenderers/ComplaintRenderer";
import { ApqRenderer } from "./NotificationRenderers/ApqRenderer";
import { PeRenderer } from "./NotificationRenderers/PeRenderer";
import { SocRenderer } from "./NotificationRenderers/SocRenderer";
import { FoiRenderer } from "./NotificationRenderers/FoiRenderer";
import { SmsRenderer } from "./NotificationRenderers/SmsRenderer";

const useStyles = makeStyles((theme) => ({
  root: {
    // maxHeight: "98%",
    // overflow: "scroll",
    paddingBottom: "15px",
    paddingTop: "55px",
    backgroundColor: "#f5f5f5",
  },
}));

type NotificationListProps = {
  dataState: NotificationsDataState;
  viewState: NotificationsViewState;
  readingNotification: (id: string) => void;
  toggleBulkModeClearNotification: (id: string, shouldClear: boolean) => void;
  pushHistoryFunc?: (url: string) => void;
};

export const NotificationList: React.FC<NotificationListProps> = ({
  dataState,
  viewState,
  readingNotification,
  toggleBulkModeClearNotification,
  pushHistoryFunc,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const toggleOpen = (isOpen: boolean) => dispatch(toggle(isOpen));
  const notifications = dataState.response;
  const history = useHistory();
  const historyPush = !!pushHistoryFunc
    ? pushHistoryFunc
    : (url: string) => history.push(url);

  return (
    <>
      <div className={classes.root}>
        {notifications.data.map((item) => {
          const propsAndRenderer = getPropsAndRenderer(
            item,
            historyPush,
            toggleOpen
          );
          if (!!propsAndRenderer) {
            const [props, component] = propsAndRenderer;
            return (
              <Notification
                key={item.id}
                notification={item}
                viewState={viewState}
                readingNotification={() => readingNotification(item.id)}
                toggleBulkModeClearNotification={(shouldClear: boolean) =>
                  toggleBulkModeClearNotification(item.id, shouldClear)
                }
                {...props}
              >
                {component}
              </Notification>
            );
          } else {
            return null;
          }
        })}
      </div>
    </>
  );
};

const getPropsAndRenderer = (
  notification: OfqualNotification,
  historyPush: (url: string) => void,
  toggleOpen: any
): [Partial<NotificationProps>, ReactNode] | null => {
  const coreProps = {};
  const body: {
    OrgId: string;
    NotificationTarget: string;
    ResourceType: string;
    Year: number;
  } = JSON.parse(notification.metaData);

  switch (notification.type) {
    case NotificationType.Default:
      return [
        {
          ...coreProps,
          actions:
            body && body.NotificationTarget
              ? [
                {
                  label: "Go to record",
                  action: () => {
                    window.location.href = `${body.NotificationTarget}`;
                    toggleOpen();
                  },
                },
              ]
              : [],
        },
        <DefaultNotificationRenderer notification={notification} />,
      ];
    case NotificationType.AOAmend:
    case NotificationType.AOAmendAccept:
    case NotificationType.AOAmendReject:
    case NotificationType.AOMessage:
    case NotificationType.AODocument:
    case NotificationType.AOClose:
    case NotificationType.AOReopen:
    case NotificationType.AOCreateRelease:
    case NotificationType.AOOrgAssigned:
      return [
        {
          ...coreProps,
          actions:
            notification.sourceId && body && body.OrgId
              ? [
                {
                  label: "Go to organisation",
                  action: () => {
                    historyPush(`/organisations/${body.OrgId}`);
                    toggleOpen();
                  },
                },
              ]
              : [],
        },
        <OrganisationRenderer notification={notification} />,
      ];
    case NotificationType.EnCreated:
    case NotificationType.EnEdited:
    case NotificationType.EnActivity:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to Event Notification",
                action: () => {
                  historyPush(
                    `/communications/event-notifications/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <EventNotificationRenderer notification={notification} />,
      ];
    case NotificationType.ExpansionApplicationMessage:
    case NotificationType.ExpansionApplicationAERRequested:
    case NotificationType.ExpansionApplicationWithdraw:
    case NotificationType.ExpansionApplicationUpdated:
    case NotificationType.ExpansionApplicationCompleted:
    case NotificationType.ExpansionApplicationCreated:
    case NotificationType.ExpansionApplicationAssigned:
    case NotificationType.ExpansionApplicationAERComplete:
      return [
        {
          ...coreProps,
          actions:
            notification.sourceId && body && body.OrgId
              ? [
                {
                  label: "Go to expansion application",
                  action: () => {
                    historyPush(
                      `/organisations/${body.OrgId}/scope/applications/${notification.sourceId}`
                    );
                    toggleOpen();
                  },
                },
              ]
              : [],
        },
        <OrganisationRenderer notification={notification} />,
      ];
    case NotificationType.RecognitionResponseDoc:
    case NotificationType.RecognitionResponseMessage:
    case NotificationType.RecognitionApplicationCreated:
    case NotificationType.RecognitionApplicationReviewerAssigned:
    case NotificationType.RecognitionInitialRegistration:
    case NotificationType.RecognitionApplicationRejectNotification:
      return [
        {
          ...coreProps,
          actions:
            notification.sourceId && body && body.OrgId
              ? [
                {
                  label: "Go to recognition application",
                  action: () => {
                    historyPush(`/organisations/${body.OrgId}/recognition`);
                    toggleOpen();
                  },
                },
              ]
              : [],
        },
        <OrganisationRenderer notification={notification} />,
      ];
    case NotificationType.InformationRequestSent:
    case NotificationType.InformationRequestAssignedOfqual:
    case NotificationType.InformationRequestUpdated:
    case NotificationType.InformationRequestRecipientAdded:
    case NotificationType.InformationRequestMessageFromOfqual:
    case NotificationType.InformationRequestAssignedAO:
    case NotificationType.InformationRequestClosed:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to request",
                action: () => {
                  historyPush(
                    `/communications/contact-ao/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <InformationRequestRenderer notification={notification} />,
      ];

    case NotificationType.InformationRequestDeleteAO:
      return [
        {
          ...coreProps,
        },
        <InformationRequestRenderer notification={notification} />,
      ];

    case NotificationType.InformationRequestMessageFromAO:
    case NotificationType.InformationRequestActionResponse:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to request",
                action: () => {
                  historyPush(
                    body && body.OrgId
                      ? `/communications/contact-ao/${notification.sourceId}/${body.OrgId}`
                      : `/communications/contact-ao/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <InformationRequestRenderer notification={notification} />,
      ];
    case NotificationType.CtrAssigned:
    case NotificationType.CtrMessageAO:
    case NotificationType.CtrMessageRegulator:
    case NotificationType.CtrCreated:
    case NotificationType.CtrClosed:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to request",
                action: () => {
                  historyPush(
                    `/communications/contact-the-regulator/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <CtrRenderer notification={notification} />,
      ];
    case NotificationType.QualificationCreatedAccredited:
    case NotificationType.QualificationMessageCreated:
    case NotificationType.QualificationApproved:
    case NotificationType.QualificationRejected:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to qualification",
                action: () => {
                  historyPush(`/qualifications/${notification.sourceId}`);
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <QualificationRenderer notification={notification} />,
      ];
    case NotificationType.ComplaintCreated:
    case NotificationType.ComplaintClosed:
    case NotificationType.ComplaintCommsCitizen:
    case NotificationType.ComplaintInternalReviewCitizen:
    case NotificationType.ComplaintStageUpdated:
    case NotificationType.ComplaintCaseLeadAssigned:
    case NotificationType.ComplaintAssigned:
    case NotificationType.ComplaintTransfer:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to complaint",
                action: () => {
                  historyPush(`/complaints/${notification.sourceId}`);
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <ComplaintRenderer notification={notification} />,
      ];
    case NotificationType.ComplaintInternalMessage:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to complaint",
                action: () => {
                  historyPush(
                    `/complaints/${notification.sourceId}/activity`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <ComplaintRenderer notification={notification} />,
      ];
    case NotificationType.ApqClosedAo:
    case NotificationType.ApqReopen:
    case NotificationType.ApqMessageFromAo:
    case NotificationType.ApqMessageFromOfqual:
    case NotificationType.ApqAttachment:
    case NotificationType.ApqFromCtr:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to APQ",
                action: () => {
                  historyPush(
                    `/assessment-plan-query/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <ApqRenderer notification={notification} />,
      ];
    case NotificationType.PeInternalMessage:
    case NotificationType.PeCitizenCreatePe:
    case NotificationType.PeTransferredFromFoi:
    case NotificationType.PeAssigned:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to public enquiry",
                action: () => {
                  historyPush(
                    `/public-enquiries/list/${notification.sourceId}`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <PeRenderer notification={notification} />,
      ];
    case NotificationType.SocInternalMessage:
    case NotificationType.SocAssign:
    case NotificationType.SocSent:
    case NotificationType.SocMessage:
    case NotificationType.SocClosed:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              body && body.Year ?
                {
                  label: "Go to SoC " + body.Year,
                  action: () => {
                    historyPush(`/soc/${body.Year}/${notification.sourceId}`);
                    toggleOpen();
                  },
                }:
              {
                label: "Go to SoC",
                action: () => {
                  historyPush(`/soc/${notification.sourceId}`);
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <SocRenderer notification={notification} />,
      ];
    case NotificationType.FoiCreatedByCitizen:
    case NotificationType.FoiAssigned:
    case NotificationType.FoiCreatedFromPublicEnquiry:
    case NotificationType.FoiCreatedFromSar:
    case NotificationType.FoiEventRecipient:
    case NotificationType.FoiInternalMessage:
    case NotificationType.FoiTransferFromPE:
    case NotificationType.FoiEventComplete:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to FOI",
                action: () => {
                  historyPush(`/foi/${notification.sourceId}`);
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <FoiRenderer notification={notification} />,
      ];
    case NotificationType.PublicMessage:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: `Go to ${body.ResourceType}`,
                action: () => {
                  historyPush(
                    `/${publicMessagePathHelper(body.ResourceType)}/${notification.sourceId
                    }`
                  );
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <PeRenderer notification={notification} />,
      ];
    case NotificationType.LcmAssigned:
    case NotificationType.LcmInternalMessage:
    case NotificationType.LcmRejected:
    case NotificationType.LcmExternalCounselRequest:
    case NotificationType.LcmExternalCounselRejected:
    case NotificationType.LcmExternalCounselAccepted:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to Legal case",
                action: () => {
                  historyPush(`/lcm/${notification.sourceId}`);
                  toggleOpen();
                },
              },
            ]
            : [],
        },
        <DefaultNotificationRenderer notification={notification} />,
      ]
    case NotificationType.SmsInternalMessage:
    case NotificationType.SmsAssignedTo:
    case NotificationType.SmsCitizenResponseToRequest:
    case NotificationType.SmsCitizenResponseToRequest:
    case NotificationType.SmsEvaluationCompleted:
    case NotificationType.SmsCitzenSubmitRequest:
    case NotificationType.SmsCitizenUpdateDetailChangeRequest:
    case NotificationType.SmsCitizenSendMessageRequest:
      return [
        {
          ...coreProps,
          actions: notification.sourceId
            ? [
              {
                label: "Go to specialist",
                action: () => {
                  historyPush(`/specialist/${notification.sourceId}`);
                  toggleOpen();
                },
              }
            ] : []
        },
        <SmsRenderer notification={notification} />
      ];
    default:
      console.warn(
        "No renderer is registered for this notification %O",
        notification
      );
      return null;
  }
};

const publicMessagePathHelper = (resourceType: string): string => {
  switch (resourceType) {
    case "PE":
      return "public-enquiries/list";
    case "FOI":
      return "foi";
    default:
      console.warn(
        "No public message path is registered for this public message type %O"
      );
      return "";
  }
};
